import { useCallback, useEffect, useRef, useState } from 'react';
import {
    sendChatMessageStreaming,
    obterMensagensDaSessao,
    obterPreferencias,
    registrarFeedback,
    type ChatAnexoRequest,
} from '@/services/assistente-ia.service';
import { fileToBase64, type AnexoComposicao, type AnexoMensagem } from '../anexos';
import { MODELO_PADRAO, ehModeloPremium } from '../modelos';
import { extrairCodigosMermaid, renderizarMermaid } from '../mermaid-renderer';
import type { ProjetoSpec } from '../projeto-spec';
import { useAuth } from '@/core/hooks/useAuth';
import { getLoginAD } from '@/core/configs/auth/auth.utils';

export type ChatAutor = 'usuario' | 'assistente';

export interface ChatMensagem {
    id: number;
    autor: ChatAutor;
    texto: string;
    anexos?: AnexoMensagem[];
    mensagemId?: number;
    feedback?: boolean | null;
    /** Bolha do assistente inserida vazia e preenchida chunk a chunk enquanto o stream corre;
     *  vira `false` só depois que qualquer diagrama Mermaid do texto final já tiver sido
     *  pré-renderizado (evita a mensagem "revelar" completa e o diagrama aparecer de repente
     *  alguns instantes depois) — nesse meio-tempo `finalizando` fica `true`. */
    streaming?: boolean;
    /** Texto já chegou por completo (stream terminou) mas ainda falta pré-renderizar diagrama(s)
     *  antes de trocar pro render markdown final — ver `streaming`. */
    finalizando?: boolean;
}

export interface UseAssistenteIAChat {
    mensagens: ChatMensagem[];
    entrada: string;
    enviando: boolean;
    erro: string | null;
    modelo: string;
    esforco: string;
    sessaoId: number | null;
    tituloSessao: string | null;
    loginAd: string;
    setModelo: (valor: string) => void;
    setEsforco: (valor: string) => void;
    setEntrada: (valor: string) => void;
    enviarMensagem: (
        texto: string,
        anexos?: AnexoComposicao[],
        comandosSelecionados?: string[],
        projetoSpec?: ProjetoSpec
    ) => Promise<void>;
    carregarSessao: (id: number) => Promise<void>;
    novaConversa: () => void;
    pararGeracao: () => void;
    avaliarMensagem: (mensagemLocalId: number, positivo: boolean) => Promise<void>;
}

export function useAssistenteIAChat(): UseAssistenteIAChat {
    const { usuarioSessao } = useAuth();
    const [mensagens, setMensagens] = useState<ChatMensagem[]>([]);
    const [entrada, setEntrada] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [modelo, setModelo] = useState<string>(MODELO_PADRAO);
    const [esforco, setEsforco] = useState<string>('baixo');
    const esforcoRef = useRef(esforco);
    esforcoRef.current = esforco;
    const [sessaoId, setSessaoId] = useState<number | null>(null);
    const [tituloSessao, setTituloSessao] = useState<string | null>(null);
    const proximoIdRef = useRef(1);
    const modeloRef = useRef(modelo);
    modeloRef.current = modelo;
    const sessaoIdRef = useRef<number | null>(null);
    sessaoIdRef.current = sessaoId;
    const loginAdRef = useRef<string>('');
    loginAdRef.current = getLoginAD(usuarioSessao);
    const abortRef = useRef<AbortController | null>(null);

    // Aborta uma geração em andamento ao desmontar (ex.: sair da página durante a resposta) —
    // evita request órfão e setState em componente já desmontado.
    useEffect(() => () => abortRef.current?.abort(), []);

    // Ao logar, restaura o último modelo Ugarqui preferido (Regra 4 + histórico de sessões) —
    // só se o usuário ainda não trocou de modelo nesta aba, e só uma vez por sessão de app.
    const preferenciaCarregadaRef = useRef(false);
    useEffect(() => {
        const loginAd = getLoginAD(usuarioSessao);
        if (!loginAd || preferenciaCarregadaRef.current) {
            return;
        }
        preferenciaCarregadaRef.current = true;
        obterPreferencias(loginAd)
            .then((preferencia) => {
                if (preferencia.modeloPreferido) {
                    setModelo(preferencia.modeloPreferido);
                }
                if (preferencia.esforcoPreferido) {
                    setEsforco(preferencia.esforcoPreferido);
                }
            })
            .catch(() => {
                // Preferência é "nice to have" — silenciosamente mantém o padrão em caso de falha.
            });
    }, [usuarioSessao]);

    const enviarMensagem = useCallback(async (
        textoBruto: string,
        anexos: AnexoComposicao[] = [],
        comandosSelecionados: string[] = [],
        projetoSpec?: ProjetoSpec
    ) => {
        const texto = textoBruto.trim();
        if ((!texto && anexos.length === 0) || enviando) {
            return;
        }

        setEntrada('');
        setErro(null);
        setEnviando(true);

        const controller = new AbortController();
        abortRef.current = controller;

        // Precisa ficar acessível no `catch` (cancelamento) para remover a bolha `streaming`
        // parcial — por isso é declarada fora do `try`, fora do escopo dos closures abaixo.
        let idMensagemAssistente: number | null = null;

        try {
            // File -> base64 (payload) + metadados/preview (bolha do usuário).
            const anexosPayload: ChatAnexoRequest[] = [];
            const anexosPreview: AnexoMensagem[] = [];
            for (const { tipo, file, url, duracaoSegundos } of anexos) {
                const conteudoBase64 = await fileToBase64(file);
                anexosPayload.push({
                    tipo,
                    mimeType: file.type,
                    nomeArquivo: file.name,
                    conteudoBase64,
                });
                anexosPreview.push({ tipo, mimeType: file.type, nomeArquivo: file.name, url, duracaoSegundos });
            }

            const mensagemUsuario: ChatMensagem = {
                id: proximoIdRef.current++,
                autor: 'usuario',
                texto,
                anexos: anexosPreview.length > 0 ? anexosPreview : undefined,
            };
            setMensagens((atual) => [...atual, mensagemUsuario]);

            // Bolha do assistente entra vazia/streaming ANTES do primeiro chunk — o loader
            // (UgarquiLoader) é quem preenche visualmente esse vazio até o texto começar a chegar.
            const idBolhaAssistente = proximoIdRef.current++;
            idMensagemAssistente = idBolhaAssistente;
            setMensagens((atual) => [
                ...atual,
                { id: idBolhaAssistente, autor: 'assistente', texto: '', streaming: true },
            ]);

            // Guardado localmente (não só no estado) pra ler o texto final completo assim que o
            // `await` abaixo resolver, sem depender de reler o state do React de forma assíncrona.
            let textoFinalAcumulado = '';

            const final = await sendChatMessageStreaming(
                texto,
                anexosPayload,
                modeloRef.current,
                esforcoRef.current,
                loginAdRef.current,
                sessaoIdRef.current ?? undefined,
                (textoParcial) => {
                    // `textoParcial` é sempre o prefixo seguro COMPLETO já emitido pelo backend —
                    // nunca um delta. Por isso SUBSTITUI o texto da bolha, nunca concatena.
                    textoFinalAcumulado = textoParcial;
                    setMensagens((atual) =>
                        atual.map((m) => (m.id === idBolhaAssistente ? { ...m, texto: textoParcial } : m))
                    );
                },
                controller.signal,
                comandosSelecionados,
                projetoSpec
            );

            // Se o texto final tem diagrama(s) Mermaid, pré-renderiza ANTES de revelar a mensagem
            // completa — sem isso, o usuário vê a mensagem "pronta" rápido (texto já chegou todo)
            // e só alguns instantes depois o diagrama aparece de repente, parecendo travado/quebrado.
            const codigosMermaid = extrairCodigosMermaid(textoFinalAcumulado);
            if (codigosMermaid.length > 0) {
                setMensagens((atual) =>
                    atual.map((m) => (m.id === idBolhaAssistente ? { ...m, finalizando: true } : m))
                );
                // Falha de um diagrama (sintaxe inválida) não pode travar a revelação da mensagem
                // inteira — o componente já trata esse caso individualmente com fallback pro código
                // cru, então aqui só esperamos a Promise resolver ou rejeitar, o que vier primeiro.
                await Promise.all(codigosMermaid.map((codigo) => renderizarMermaid(codigo).catch(() => undefined)));
            }

            setMensagens((atual) =>
                atual.map((m) =>
                    m.id === idBolhaAssistente
                        ? {
                              ...m,
                              streaming: false,
                              finalizando: false,
                              mensagemId: final.mensagemIdAssistente ?? undefined,
                              feedback: null,
                          }
                        : m
                )
            );

            if (final.sessaoId) {
                setSessaoId(final.sessaoId);
            }
            if (final.titulo) {
                setTituloSessao(final.titulo);
            }
        } catch (error) {
            // Cancelamento voluntário (botão Parar) ou falha real (erro de rede/servidor) — nos
            // dois casos a bolha streaming parcial (se já tinha sido inserida) precisa sair da
            // tela, senão o loader (UgarquiLoader) fica preso mostrando "Pensando..." pra sempre,
            // já que ele reage a `mensagem.streaming`, não a `enviando`.
            if (idMensagemAssistente !== null) {
                setMensagens((atual) => atual.filter((m) => m.id !== idMensagemAssistente));
            }
            if ((error as { code?: string })?.code !== 'ERR_CANCELED') {
                const mensagem =
                    error instanceof Error ? error.message : 'Não foi possível enviar sua mensagem.';
                setErro(mensagem);
            }
        } finally {
            abortRef.current = null;
            setEnviando(false);
        }
    }, [enviando]);

    const pararGeracao = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    const carregarSessao = useCallback(async (id: number) => {
        const loginAd = loginAdRef.current;
        if (!loginAd || enviando) {
            return;
        }

        setErro(null);
        try {
            const detalhe = await obterMensagensDaSessao(id, loginAd);
            const mensagensCarregadas: ChatMensagem[] = detalhe.mensagens.map((mensagem) => ({
                id: proximoIdRef.current++,
                autor: mensagem.papel,
                texto: mensagem.conteudo,
                mensagemId: mensagem.id,
                feedback: mensagem.feedback ?? null,
            }));

            setMensagens(mensagensCarregadas);
            setSessaoId(detalhe.id);
            setTituloSessao(detalhe.titulo || null);
            if (detalhe.modeloId) {
                setModelo(detalhe.modeloId);
            }
        } catch (error) {
            const mensagem =
                error instanceof Error ? error.message : 'Não foi possível carregar essa conversa.';
            setErro(mensagem);
        }
    }, [enviando]);

    const novaConversa = useCallback(() => {
        setMensagens([]);
        setSessaoId(null);
        setTituloSessao(null);
        setErro(null);
    }, []);

    // Regra Fase 6: thumbs up/down numa resposta do assistente. `mensagemLocalId` é o `id` local
    // da bolha (React key); resolve-se o `mensagemId` (backend) a partir do estado atual antes de
    // chamar a API, e só atualiza o voto na tela em caso de sucesso.
    const avaliarMensagem = useCallback(async (mensagemLocalId: number, positivo: boolean) => {
        const loginAd = loginAdRef.current;
        if (!loginAd) {
            return;
        }
        const alvo = mensagens.find((mensagem) => mensagem.id === mensagemLocalId);
        if (!alvo?.mensagemId) {
            return;
        }

        try {
            await registrarFeedback(alvo.mensagemId, loginAd, positivo);
            setMensagens((atual) =>
                atual.map((mensagem) =>
                    mensagem.id === mensagemLocalId ? { ...mensagem, feedback: positivo } : mensagem
                )
            );
        } catch {
            // Feedback é "nice to have" — falha silenciosa não deve interromper o chat.
        }
    }, [mensagens]);

    return {
        mensagens,
        entrada,
        enviando,
        erro,
        modelo,
        esforco,
        sessaoId,
        tituloSessao,
        loginAd: loginAdRef.current,
        setModelo,
        setEsforco,
        setEntrada,
        enviarMensagem,
        carregarSessao,
        novaConversa,
        pararGeracao,
        avaliarMensagem,
    };
}
